import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Eye, 
  Edit, 
  User, 
  Search, 
  RefreshCw, 
  Filter,
  Users,
  UserCheck,
  UserX,
  Building
} from 'lucide-react';
import { usuariosApi, Usuario } from '../../apis/usuariosApi';
import { toast } from 'react-toastify';

const ListaUsuarios: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlataforma, setSelectedPlataforma] = useState<string>('');
  const [filteredUsuarios, setFilteredUsuarios] = useState<Usuario[]>([]);

  const { data: usuarios, isLoading, error, refetch } = useQuery<Usuario[]>({
    queryKey: ['usuarios'],
    queryFn: usuariosApi.getAll
  });

  const { data: plataformas } = useQuery({
    queryKey: ['plataformas-usuarios'],
    queryFn: usuariosApi.getPlataformas
  });

  useEffect(() => {
    if (error) {
      toast.error('Error al cargar usuarios');
    }
  }, [error]);

  useEffect(() => {
    if (usuarios) {
      filterUsuarios();
    }
  }, [usuarios, searchTerm, selectedPlataforma]);

  const filterUsuarios = () => {
    if (!usuarios) return;

    let filtered = usuarios;

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(usuario =>
        usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.apellido_paterno.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.correo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.rol.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por plataforma
    if (selectedPlataforma) {
      filtered = filtered.filter(usuario => 
        usuario.plataforma.slug === selectedPlataforma
      );
    }

    setFilteredUsuarios(filtered);
  };

  const handleRefresh = () => {
    refetch();
    toast.info('Actualizando lista de usuarios...');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedPlataforma('');
  };

  // Estadísticas
  const totalUsuarios = usuarios?.length || 0;
  const usuariosActivos = usuarios?.filter(u => u.estatus === 1).length || 0;
  const usuariosInactivos = totalUsuarios - usuariosActivos;
  const plataformasUnicas = new Set(usuarios?.map(u => u.plataforma.nombre)).size;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-300">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">Error al cargar usuarios</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .usuarios-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
            color: white;
            padding: 2rem;
          }

          .content-wrapper {
            max-width: 1400px;
            margin: 0 auto;
          }

          .page-header {
            text-align: left;
            margin-bottom: 3rem;
          }

          .page-title {
            font-size: 2.5rem;
            font-weight: 700;
            background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
          }

          .page-subtitle {
            color: rgba(255, 255, 255, 0.7);
            font-size: 1.1rem;
          }

          .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
            margin-bottom: 2rem;
          }

          .stat-card {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
            transition: all 0.3s ease;
          }

          .stat-card:hover {
            background: rgba(255, 255, 255, 0.08);
            border-color: rgba(0, 122, 255, 0.3);
            transform: translateY(-2px);
          }

          .stat-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 1rem;
          }

          .stat-title {
            color: rgba(255, 255, 255, 0.8);
            font-size: 0.9rem;
            font-weight: 500;
          }

          .stat-value {
            font-size: 2rem;
            font-weight: 700;
            color: white;
          }

          .controls-section {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
            margin-bottom: 2rem;
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            align-items: center;
            justify-content: space-between;
          }

          .search-container {
            position: relative;
            flex: 1;
            max-width: 400px;
          }

          .search-input {
            width: 100%;
            padding: 12px 16px 12px 48px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            color: white;
            font-size: 1rem;
            transition: all 0.3s ease;
          }

          .search-input:focus {
            outline: none;
            background: rgba(255, 255, 255, 0.15);
            border-color: #007AFF;
            box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
          }

          .search-input::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }

          .search-icon {
            position: absolute;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            color: rgba(255, 255, 255, 0.5);
          }

          .filter-select {
            padding: 12px 16px 12px 48px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            color: white;
            font-size: 1rem;
            min-width: 200px;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .filter-select:focus {
            outline: none;
            background: rgba(255, 255, 255, 0.15);
            border-color: #007AFF;
          }

          .filter-select option {
            background: #1a1a1a;
            color: white;
          }

          .action-buttons {
            display: flex;
            gap: 1rem;
          }

          .btn {
            padding: 12px 20px;
            border-radius: 12px;
            font-weight: 500;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
            border: none;
            cursor: pointer;
            font-size: 1rem;
          }

          .btn-primary {
            background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
            color: white;
          }

          .btn-primary:hover {
            background: linear-gradient(135deg, #0056CC 0%, #003D99 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 122, 255, 0.3);
          }

          .btn-secondary {
            background: rgba(255, 255, 255, 0.1);
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .btn-secondary:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          .table-container {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            overflow: hidden;
          }

          .table {
            width: 100%;
            border-collapse: collapse;
          }

          .table th {
            background: rgba(255, 255, 255, 0.1);
            padding: 1rem;
            text-align: left;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.9);
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .table td {
            padding: 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          }

          .table tr:hover {
            background: rgba(255, 255, 255, 0.05);
          }

          .user-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: rgba(0, 122, 255, 0.2);
            border: 1px solid rgba(0, 122, 255, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 1rem;
          }

          .badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 500;
            border: 1px solid;
          }

          .badge-blue {
            background: rgba(0, 122, 255, 0.2);
            color: #007AFF;
            border-color: rgba(0, 122, 255, 0.3);
          }

          .badge-green {
            background: rgba(52, 199, 89, 0.2);
            color: #34C759;
            border-color: rgba(52, 199, 89, 0.3);
          }

          .badge-red {
            background: rgba(255, 59, 48, 0.2);
            color: #FF3B30;
            border-color: rgba(255, 59, 48, 0.3);
          }

          .action-btn {
            padding: 8px;
            border-radius: 8px;
            border: none;
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.7);
            cursor: pointer;
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .action-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            color: white;
          }

          .empty-state {
            text-align: center;
            padding: 4rem 2rem;
            color: rgba(255, 255, 255, 0.6);
          }

          .results-info {
            text-align: center;
            color: rgba(255, 255, 255, 0.6);
            font-size: 0.875rem;
            margin-top: 1rem;
          }
        `
      }} />

      <div className="usuarios-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="page-header">
            <h1 className="page-title">Gestión de Usuarios</h1>
            <p className="page-subtitle">Administra los usuarios del sistema</p>
          </div>

          {/* Estadísticas */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-title">Total</span>
                <Users size={20} color="#007AFF" />
              </div>
              <div className="stat-value">{totalUsuarios}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-title">Activos</span>
                <UserCheck size={20} color="#34C759" />
              </div>
              <div className="stat-value">{usuariosActivos}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-title">Inactivos</span>
                <UserX size={20} color="#FF3B30" />
              </div>
              <div className="stat-value">{usuariosInactivos}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-title">Plataformas</span>
                <Building size={20} color="#007AFF" />
              </div>
              <div className="stat-value">{plataformasUnicas}</div>
            </div>
          </div>

          {/* Controles */}
          <div className="controls-section">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div style={{ position: 'relative' }}>
              <Filter style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255, 255, 255, 0.5)' }} size={16} />
              <select
                value={selectedPlataforma}
                onChange={(e) => setSelectedPlataforma(e.target.value)}
                className="filter-select"
              >
                <option value="">Todas las plataformas</option>
                {plataformas?.map((plataforma) => (
                  <option key={plataforma.id} value={plataforma.slug}>
                    {plataforma.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="action-buttons">
              {(searchTerm || selectedPlataforma) && (
                <button onClick={clearFilters} className="btn btn-secondary">
                  Limpiar
                </button>
              )}
              
              <button onClick={handleRefresh} className="btn btn-secondary">
                <RefreshCw size={16} />
                Actualizar
              </button>
              
              <Link to="/usuarios/crear" className="btn btn-primary">
                <Plus size={16} />
                Crear Usuario
              </Link>
            </div>
          </div>

          {/* Tabla */}
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Correo</th>
                  <th>Rol</th>
                  <th>Plataforma</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map((usuario) => (
                  <tr key={usuario.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div className="user-avatar">
                          <User size={20} color="#007AFF" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', color: 'white', marginBottom: '4px' }}>
                            {usuario.nombre} {usuario.apellido_paterno}
                          </div>
                          {usuario.apellido_materno && (
                            <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                              {usuario.apellido_materno}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{usuario.correo}</td>
                    <td>
                      <span className="badge badge-blue">{usuario.rol.nombre}</span>
                    </td>
                    <td>
                      <span className="badge badge-green">{usuario.plataforma.nombre}</span>
                    </td>
                    <td>
                      <span className={`badge ${usuario.estatus === 1 ? 'badge-green' : 'badge-red'}`}>
                        {usuario.estatus === 1 ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Link to={`/usuarios/${usuario.id}`} className="action-btn" title="Ver detalles">
                          <Eye size={16} />
                        </Link>
                        <Link to={`/usuarios/${usuario.id}/editar`} className="action-btn" title="Editar">
                          <Edit size={16} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Estados vacíos */}
          {filteredUsuarios.length === 0 && usuarios && usuarios.length > 0 && (
            <div className="empty-state">
              <Search size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: 'white', marginBottom: '0.5rem' }}>
                No se encontraron usuarios
              </h3>
              <p style={{ marginBottom: '1.5rem' }}>
                Intenta ajustar los filtros de búsqueda
              </p>
              <button onClick={clearFilters} className="btn btn-primary">
                Limpiar filtros
              </button>
            </div>
          )}

          {usuarios && usuarios.length === 0 && (
            <div className="empty-state">
              <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: 'white', marginBottom: '0.5rem' }}>
                No hay usuarios
              </h3>
              <p style={{ marginBottom: '1.5rem' }}>
                Comienza creando tu primer usuario.
              </p>
              <Link to="/usuarios/crear" className="btn btn-primary">
                <Plus size={16} />
                Crear Usuario
              </Link>
            </div>
          )}

          {/* Info de resultados */}
          {filteredUsuarios.length > 0 && (
            <div className="results-info">
              Mostrando {filteredUsuarios.length} de {totalUsuarios} usuarios
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ListaUsuarios;
